import { ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import ChartCard from "./ChartCard";
import KpiCard from "./KpiCard";
import ManagementAttention from "./ManagementAttention";
import { AfLineChart, CashChart, CompanyBars } from "./charts";
import { MONTH_LABELS, fmtMonths, fmtPct, fmtRate, fmtSEK } from "./dashUtils";

const COMPANY_COLORS = ["#0052CC", "#38bdf8", "#f59e0b", "#94a3b8", "#64748b"];
const shortName = (n) => n.replace("Elva ", "").replace(" AB", "");

function YearCompanyBars({ years, seriesByCompany, valueFormatter, testId, label }) {
  const data = years.map((y) => {
    const row = { year: y };
    for (const [company, byYear] of Object.entries(seriesByCompany)) {
      row[company] = byYear[y]?.value ?? null;
    }
    return row;
  });
  const companies = Object.keys(seriesByCompany);
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} data-testid={testId}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="year" fontSize={10} tick={{ fill: "#94a3b8" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
        <YAxis fontSize={10} tick={{ fill: "#94a3b8" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} width={44} tickFormatter={valueFormatter} />
        <Tooltip formatter={(v) => (v == null ? "N/A" : valueFormatter(v))} contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e2e8f0" }} />
        {companies.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {companies.map((c, i) => (
          <Bar key={c} dataKey={c} name={shortName(c)} fill={COMPANY_COLORS[i % COMPANY_COLORS.length]} radius={[3, 3, 0, 0]} isAnimationActive={false} />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export default function GroupDashboard({ data }) {
  const months = data.months.map((m) => ({ ...m, label: MONTH_LABELS[m.month - 1] }));
  const cum = [];
  let sRun = 0, pRun = 0;
  for (const m of months) {
    sRun += m.sales || 0;
    pRun += m.pbt || 0;
    cum.push({ ...m, sales: sRun, pbt: pRun });
  }
  const t = data.targets;
  const s = data.summary;

  return (
    <div className="space-y-4" data-testid="group-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="group-summary">
        <KpiCard testId="kpi-cons-sales" label="Consolidated Sales" value={`${fmtSEK(s.consolidated_sales)} kr`} sub={`Expected outcome ${data.year} (IC eliminated)`} />
        <KpiCard testId="kpi-cons-pbt" label="Consolidated PBT" value={`${fmtSEK(s.consolidated_pbt)} kr`} sub="External economics only" />
        <KpiCard testId="kpi-oh-ratio" label="Overhead Cost Ratio" value={fmtPct(s.overhead_cost_ratio)}
                 sub={t.group_overhead != null ? `Target ≤ ${fmtPct(t.group_overhead)}` : "Target not configured"}
                 tone={t.group_overhead != null && s.overhead_cost_ratio != null && s.overhead_cost_ratio > t.group_overhead ? "warning" : "default"} />
        <KpiCard testId="kpi-group-cash" label="Closing Cash (Dec)" value={`${fmtSEK(s.closing_cash)} kr`} sub="Legal entities summed, IC eliminated" />
        <KpiCard testId="kpi-group-survival" label="Survival" value={s.survival_months != null ? `${fmtMonths(s.survival_months)} months` : "N/A"}
                 sub={t.survival_months != null ? `Target ${fmtMonths(t.survival_months)} months` : "Target not configured"} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChartCard title="Group Sales & PBT" subtitle="Consolidated, cumulative — intercompany eliminated" linkTo="/group/consolidation" linkLabel="Consolidation" testId="chart-group-sales-pbt">
          <AfLineChart months={cum} testId="chart-group-sales-pbt-c" formatter={(v) => `${Math.round(v / 1000)}k`}
                       series={[{ key: "sales", label: "Sales", color: "#0052CC" }, { key: "pbt", label: "PBT", color: "#f59e0b" }]} />
        </ChartCard>

        <ChartCard title="Group Cash Flow & Closing Cash" subtitle="Consolidated cash (legal entities summed, intercompany eliminated)" linkTo="/financials/cash-flow" linkLabel="Cash Flow" testId="chart-group-cash">
          <CashChart months={months} testId="chart-group-cash-c" />
        </ChartCard>

        <ChartCard title="Overhead Cost Ratio" subtitle="Cumulative YTD group overhead / consolidated external sales" linkTo="/group/overhead" linkLabel="Group Overhead" testId="chart-group-oh">
          <AfLineChart months={months} testId="chart-group-oh-c" formatter={(v) => `${v}%`}
                       series={[{ key: "overhead_cost_ratio_ytd", label: "Overhead Cost Ratio YTD", color: "#0052CC" }]}
                       targets={[{ label: "Target", value: t.group_overhead, color: "#f59e0b" }]} />
        </ChartCard>

        <ChartCard title="Financial Benchmark" subtitle={`Operating companies, ${data.year} — cost/margin structure`} linkTo="/group/overview" linkLabel="Group Overview" testId="chart-fin-benchmark">
          <CompanyBars rows={data.benchmarks} testId="chart-fin-benchmark-c" formatter={(v) => `${v}%`}
                       series={[{ key: "direct_cost_pct", label: "Direct Cost %" }, { key: "gross_margin", label: "Gross Margin %" },
                                { key: "indirect_cost_pct", label: "Indirect Cost %" }, { key: "net_margin", label: "Net Margin %" }]} />
        </ChartCard>

        <ChartCard title="Average Rate by Company" subtitle={`Annual benchmark, ${data.year} (weighted T&M rate)`} linkTo="/planning/resource-planning" linkLabel="Resource Planning" testId="chart-rate-benchmark">
          <CompanyBars rows={data.benchmarks} testId="chart-rate-benchmark-c" formatter={(v) => fmtSEK(v)}
                       series={[{ key: "average_rate", label: "Average Rate (kr/h)" }]} />
        </ChartCard>

        <ChartCard title="Utilization by Company" subtitle={`Annual benchmark, ${data.year} (billable / capacity)`} linkTo="/planning/resource-planning" linkLabel="Resource Planning" testId="chart-util-benchmark">
          <CompanyBars rows={data.benchmarks} testId="chart-util-benchmark-c" formatter={(v) => `${v}%`}
                       series={[{ key: "utilization", label: "Utilization %" }]} />
        </ChartCard>

        <ChartCard title="Average Rate — History & Outlook by Company" subtitle="Annual (actual / expected / forecast)" testId="chart-rate-history">
          <YearCompanyBars years={data.history.years} seriesByCompany={data.history.average_rate} valueFormatter={(v) => fmtSEK(v)} testId="chart-rate-history-c" label="Average Rate" />
        </ChartCard>

        <ChartCard title="Utilization — History & Outlook by Company" subtitle="Annual (actual / expected / forecast)" testId="chart-util-history">
          <YearCompanyBars years={data.history.years} seriesByCompany={data.history.utilization} valueFormatter={(v) => `${v}%`} testId="chart-util-history-c" label="Utilization" />
        </ChartCard>
      </div>

      <ManagementAttention signals={data.attention} testId="group-attention" />
    </div>
  );
}
